/**
 * @file GraphThrottleError.ts
 * @description Erreur typée levée quand un appel Microsoft Graph reste en 429
 * (Too Many Requests) après épuisement des tentatives de backoff dans
 * `GraphManager.callGraphEndPoint`.
 *
 * Depuis l'unification du throttling (PBI #1666), ce type est une **spécialisation**
 * de {@link ThrottleError} (module partagé `@/lib/utils`) : il en hérite `status: 429`,
 * `isThrottle: true`, `endpoint` et `attempts`. On conserve un type dédié (et son `name`
 * `GraphThrottleError`) pour ne pas casser les imports / `instanceof` existants côté Graph.
 *
 * Le flag `isThrottle` permet une détection sans import (duck-typing), utile pour
 * les couches qui ne doivent pas dépendre du package `graph`.
 *
 * `isThrottleError` est ré-exporté depuis le module partagé : il détecte indifféremment
 * une `GraphThrottleError`, une `ThrottleError` ou tout objet duck-typé `{ isThrottle: true }`.
 */
import { ThrottleError } from "../../../utils/throttle";

export class GraphThrottleError extends ThrottleError {
    constructor(endpoint: string, attempts: number) {
        super(
            endpoint,
            attempts,
            `Graph throttled (429) on ${endpoint} after ${attempts} retr${attempts === 1 ? "y" : "ies"}`,
        );
        this.name = "GraphThrottleError";
        // `new.target` dans ThrottleError restaure déjà le prototype GraphThrottleError.
    }
}

export { isThrottleError } from "../../../utils/throttle";
